import React, { useEffect, useState, useMemo } from 'react';
import { analyticsAPI } from '@/services/api';
import { Flame, TrendingUp, Sparkles, Zap, Filter, Tag } from 'lucide-react';

const CATEGORY_COLORS = {
  hot: ['#EF4444', '#F97316', '#DC2626', '#FB923C', '#F43F5E'],
  trending: ['#3B82F6', '#6366F1', '#8B5CF6', '#2563EB', '#7C3AED'],
  emerging: ['#10B981', '#14B8A6', '#22C55E', '#059669', '#06B6D4'],
  niche: ['#6B7280', '#9CA3AF', '#78716C', '#71717A', '#A1A1AA'],
};

const TAG_CATEGORIES = [
  { value: '', label: 'All categories', color: '#A1A1AA' },
  { value: 'technology', label: 'Technology', color: '#38BDF8' },
  { value: 'tools', label: 'Tools', color: '#C084FC' },
  { value: 'methodology', label: 'Methodology', color: '#FBBF24' },
  { value: 'domain', label: 'Domain', color: '#34D399' },
  { value: 'behavioral', label: 'Behavioral', color: '#F472B6' },
  { value: 'role', label: 'Role / Function', color: '#A5B4FC' },
];

const INDUSTRIES = [
  'All Industries', 'Information Technology', 'FinTech', 'HealthTech', 'EdTech',
  'E-Commerce', 'Manufacturing', 'Banking & Finance', 'Consulting',
  'Media & Entertainment', 'Logistics', 'Retail', 'Telecom', 'Real Estate',
];

export default function SkillExplorer() {
  const [skills, setSkills] = useState([]);
  const [industry, setIndustry] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [hoveredSkill, setHoveredSkill] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const d = category
          ? await analyticsAPI.getCategorizedWordcloud(industry, category)
          : await (async () => {
              const API_URL = process.env.REACT_APP_BACKEND_URL;
              const token = localStorage.getItem('auth_token');
              const q = industry ? `?industry=${encodeURIComponent(industry)}` : '';
              const r = await fetch(`${API_URL}/api/analytics/skill-wordcloud-full${q}`, { headers: { 'Authorization': `Bearer ${token}` } });
              return r.json();
            })();
        const list = (d.skills || []).map(s => ({
          ...s,
          // categorized endpoint uses `heat`; full endpoint uses `category` for heat
          heat: s.heat || s.category,
          tag_category: s.heat ? s.category : null,
        }));
        setSkills(list);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [industry, category]);

  const maxCount = useMemo(() => Math.max(...skills.map(s => s.count), 1), [skills]);

  // Smaller base sizes — only the very top skills get large
  const getSize = (count) => {
    const ratio = count / maxCount;
    if (ratio > 0.85) return 'text-2xl font-black';
    if (ratio > 0.7) return 'text-xl font-extrabold';
    if (ratio > 0.55) return 'text-lg font-bold';
    if (ratio > 0.4) return 'text-base font-semibold';
    if (ratio > 0.25) return 'text-sm font-medium';
    if (ratio > 0.12) return 'text-xs font-medium';
    return 'text-[11px] font-normal';
  };

  const getColor = (skill) => {
    const heat = skill.heat || skill.category;
    const colors = CATEGORY_COLORS[heat] || CATEGORY_COLORS.niche;
    const hash = skill.skill.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const getRotation = (skill) => {
    const hash = skill.skill.length * 7 + skill.count;
    const angles = [0, 0, 0, -6, 4, -4, 8, 0, 0, -2, 0, 0, 3, 0, -3, 0, 0, 5];
    return angles[hash % angles.length];
  };

  const categoryStats = useMemo(() => {
    const stats = { hot: 0, trending: 0, emerging: 0, niche: 0 };
    skills.forEach(s => { const h = s.heat || s.category; stats[h] = (stats[h] || 0) + 1; });
    return stats;
  }, [skills]);

  return (
    <div data-testid="skill-explorer">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Skill Explorer</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">Discover in-demand skills, tools, methodologies and domains across the job market.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-slate-500 dark:text-zinc-500" />
          <select value={industry} onChange={e => setIndustry(e.target.value)}
            className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-zinc-300" data-testid="industry-filter-full">
            {INDUSTRIES.map(i => <option key={i} value={i === 'All Industries' ? '' : i}>{i}</option>)}
          </select>
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-zinc-300" data-testid="category-filter">
            {TAG_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      {/* Category pill row */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="category-pill-row">
        {TAG_CATEGORIES.map(c => (
          <button key={c.value || 'all'} onClick={() => setCategory(c.value)}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${category === c.value ? 'bg-slate-900 dark:bg-white text-white dark:text-black border-slate-900 dark:border-white' : 'bg-white dark:bg-[#111114] text-slate-500 dark:text-zinc-400 border-slate-200 dark:border-zinc-800 hover:border-slate-300 dark:hover:border-zinc-600'}`}
            data-testid={`cat-pill-${c.value || 'all'}`}>
            <Tag className="w-3 h-3 inline mr-1" style={{ color: category === c.value ? '#111' : c.color }} />
            {c.label}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-6">
        <LegendItem icon={Flame} label="Hot" count={categoryStats.hot} color="#EF4444" />
        <LegendItem icon={TrendingUp} label="Trending" count={categoryStats.trending} color="#3B82F6" />
        <LegendItem icon={Sparkles} label="Emerging" count={categoryStats.emerging} color="#10B981" />
        <LegendItem icon={Zap} label="Niche" count={categoryStats.niche} color="#6B7280" />
      </div>

      {loading ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-16 text-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-500 dark:text-zinc-500">Loading skills...</p>
        </div>
      ) : skills.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-16 text-center">
          <p className="text-slate-500 dark:text-zinc-500">No skill data available for this industry</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-6 min-h-[420px] relative overflow-hidden" data-testid="word-cloud-area">
          {/* Background glow effects */}
          <div className="absolute top-1/4 left-1/4 w-48 h-48 bg-red-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 right-1/4 w-40 h-40 bg-blue-500/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 right-1/3 w-36 h-36 bg-emerald-500/5 rounded-full blur-3xl" />

          <div className="flex flex-wrap gap-x-3 gap-y-1.5 items-baseline justify-center relative z-10">
            {skills.map(skill => (
              <span key={skill.skill}
                className={`${getSize(skill.count)} cursor-default transition-all duration-200 hover:scale-110 hover:opacity-100 leading-tight ${hoveredSkill && hoveredSkill !== skill.skill ? 'opacity-30' : 'opacity-85'}`}
                style={{
                  color: getColor(skill),
                  transform: `rotate(${getRotation(skill)}deg)`,
                  textShadow: (skill.heat || skill.category) === 'hot' ? `0 0 16px ${getColor(skill)}30` : 'none',
                }}
                onMouseEnter={() => setHoveredSkill(skill.skill)}
                onMouseLeave={() => setHoveredSkill(null)}
                title={`${skill.skill}: ${skill.count} postings (${skill.pct}%) — ${skill.heat || skill.category}${skill.tag_category ? ' · ' + skill.tag_category : ''}`}
                data-testid={`cloud-skill-${skill.skill}`}>
                {skill.skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Tooltip info when hovering */}
      {hoveredSkill && (
        <div className="mt-4 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg px-4 py-3 inline-flex items-center gap-3">
          <span className="text-slate-900 dark:text-white font-medium">{hoveredSkill}</span>
          {skills.find(s => s.skill === hoveredSkill) && (() => {
            const s = skills.find(sk => sk.skill === hoveredSkill);
            const heat = s.heat || s.category;
            return (<>
              <span className="text-slate-500 dark:text-zinc-500 text-sm">{s.pct}% of postings</span>
              {s.tag_category && <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-sky-500/10 text-sky-700 dark:text-sky-300">{s.tag_category}</span>}
              <span className={`text-xs px-2 py-0.5 rounded capitalize ${heat === 'hot' ? 'bg-red-500/10 text-red-600 dark:text-red-400' : heat === 'trending' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : heat === 'emerging' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400'}`}>{heat}</span>
            </>);
          })()}
        </div>
      )}
    </div>
  );
}

function LegendItem({ icon: Icon, label, count, color }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <span className="text-xs" style={{ color }}>{label}</span>
      <span className="text-slate-500 dark:text-zinc-600 text-xs">({count})</span>
    </div>
  );
}
